import React, { useState, useEffect, useCallback } from 'react';
import { ThemeToggle } from './theme';
import Icon from './icons';
import macActivationToolDownload from './Assets/Adobe Activation Tool v1.2.7 macOS.zip';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// "Didn't get your Adobe invitation? / Resend my invitation" box.
// Temporarily hidden at the owner's request — set back to true to restore it.
const SHOW_SELF_REINVITE = false;

// Translations for multiple languages
const translations = {
    en: {
        title: "Check Your Subscription",
        subtitle: "Enter your email to view your Adobe subscription status",
        emailPlaceholder: "Enter your email address",
        searchButton: "Check Status",
        searching: "Searching...",
        organization: "Team Name",
        plan: "Active Plan",
        duration: "Duration",
        months: "months",
        daysRemaining: "Days Remaining",
        days: "days",
        status: "Status",
        activated: "Activated",
        expires: "Expires",
        active: "Active",
        expired: "Expired",
        expiringSoon: "Expiring Soon",
        notFound: "No subscription found for this email",
        notFoundDesc: "Please check your email address or contact your reseller.",
        instructions: "Instructions",
        instructionsText: "If you recently received access or your organization was updated, please sign out of all Adobe apps and sign back in with this email. Go to account.adobe.com and select 'Sign out everywhere' if needed.",
        teamSwitch: "Need to switch teams?",
        teamSwitchText: "If you see multiple team options, select the organization shown above to access your subscription.",
        videoTitle: "How do I delete an invalid organization?",
        notice: "Important Notice",
        subscriptionProgress: "Subscription Progress",
        footer: "For support, contact your reseller or administrator.",
        language: "Language",
        // Migration-specific translations
        migrationInProgress: "Migration In Progress",
        migrationSubtitle: "Your account is being upgraded to a new system",
        queuePosition: "Queue Position",
        estimatedTime: "Estimated Time",
        minutes: "min",
        usersProcessed: "Users Processed",
        pleaseWait: "Please wait while we complete your migration. This process is automatic.",
        migrationActive: "MIGRATING"
    },
    es: {
        title: "Consulta tu Suscripción",
        subtitle: "Ingresa tu correo para ver el estado de tu suscripción de Adobe",
        emailPlaceholder: "Ingresa tu correo electrónico",
        searchButton: "Consultar Estado",
        searching: "Buscando...",
        organization: "Nombre del equipo",
        plan: "Plan Activo",
        duration: "Duración",
        months: "meses",
        daysRemaining: "Días Restantes",
        days: "días",
        status: "Estado",
        activated: "Activado",
        expires: "Expira",
        active: "Activo",
        expired: "Expirado",
        expiringSoon: "Por Expirar",
        notFound: "No se encontró suscripción para este correo",
        notFoundDesc: "Por favor verifica tu correo o contacta a tu revendedor.",
        instructions: "Instrucciones",
        instructionsText: "Si recientemente obtuviste acceso o tu organización fue actualizada, cierra sesión en todas las apps de Adobe y vuelve a iniciar sesión con este correo. Ve a account.adobe.com y selecciona 'Cerrar sesión en todos lados' si es necesario.",
        teamSwitch: "¿Necesitas cambiar de equipo?",
        teamSwitchText: "Si ves múltiples opciones de equipo, selecciona la organización mostrada arriba para acceder a tu suscripción.",
        videoTitle: "¿Cómo elimino una organización inválida?",
        notice: "Aviso Importante",
        subscriptionProgress: "Progreso de Suscripción",
        footer: "Para soporte, contacta a tu revendedor o administrador.",
        language: "Idioma",
        migrationInProgress: "Migración en Progreso",
        migrationSubtitle: "Tu cuenta está siendo actualizada a un nuevo sistema",
        queuePosition: "Posición en Cola",
        estimatedTime: "Tiempo Estimado",
        minutes: "min",
        usersProcessed: "Usuarios Procesados",
        pleaseWait: "Por favor espera mientras completamos tu migración. Este proceso es automático.",
        migrationActive: "MIGRANDO"
    },
    fr: {
        title: "Vérifiez Votre Abonnement",
        subtitle: "Entrez votre email pour voir le statut de votre abonnement Adobe",
        emailPlaceholder: "Entrez votre adresse email",
        searchButton: "Vérifier le Statut",
        searching: "Recherche...",
        organization: "Nom de l'équipe",
        plan: "Plan Actif",
        duration: "Durée",
        months: "mois",
        daysRemaining: "Jours Restants",
        days: "jours",
        status: "Statut",
        activated: "Activé",
        expires: "Expire",
        active: "Actif",
        expired: "Expiré",
        expiringSoon: "Expire Bientôt",
        notFound: "Aucun abonnement trouvé pour cet email",
        notFoundDesc: "Veuillez vérifier votre email ou contacter votre revendeur.",
        instructions: "Instructions",
        instructionsText: "Si vous avez récemment reçu l'accès ou si votre organisation a été mise à jour, déconnectez-vous de toutes les applications Adobe et reconnectez-vous avec cet email. Allez sur account.adobe.com et sélectionnez 'Se déconnecter partout' si nécessaire.",
        teamSwitch: "Besoin de changer d'équipe?",
        teamSwitchText: "Si vous voyez plusieurs options d'équipe, sélectionnez l'organisation indiquée ci-dessus pour accéder à votre abonnement.",
        videoTitle: "Comment supprimer une organisation invalide?",
        notice: "Avis Important",
        subscriptionProgress: "Progression de l'Abonnement",
        footer: "Pour le support, contactez votre revendeur ou administrateur.",
        language: "Langue",
    },
    de: {
        title: "Abonnement Überprüfen",
        subtitle: "Geben Sie Ihre E-Mail ein, um Ihren Adobe-Abonnementstatus anzuzeigen",
        emailPlaceholder: "E-Mail-Adresse eingeben",
        searchButton: "Status Prüfen",
        searching: "Suche...",
        organization: "Teamname",
        plan: "Aktiver Plan",
        duration: "Dauer",
        months: "Monate",
        daysRemaining: "Verbleibende Tage",
        days: "Tage",
        status: "Status",
        activated: "Aktiviert",
        expires: "Läuft ab",
        active: "Aktiv",
        expired: "Abgelaufen",
        expiringSoon: "Läuft bald ab",
        notFound: "Kein Abonnement für diese E-Mail gefunden",
        notFoundDesc: "Bitte überprüfen Sie Ihre E-Mail oder kontaktieren Sie Ihren Händler.",
        instructions: "Anleitung",
        instructionsText: "Wenn Sie kürzlich Zugang erhalten haben oder Ihre Organisation aktualisiert wurde, melden Sie sich von allen Adobe-Apps ab und melden Sie sich erneut mit dieser E-Mail an. Gehen Sie zu account.adobe.com und wählen Sie 'Überall abmelden' falls nötig.",
        teamSwitch: "Team wechseln?",
        teamSwitchText: "Wenn Sie mehrere Teamoptionen sehen, wählen Sie die oben angezeigte Organisation, um auf Ihr Abonnement zuzugreifen.",
        videoTitle: "Wie lösche ich eine ungültige Organisation?",
        notice: "Wichtiger Hinweis",
        subscriptionProgress: "Abonnementfortschritt",
        footer: "Für Support kontaktieren Sie Ihren Händler oder Administrator.",
        language: "Sprache",
    },
    pt: {
        title: "Verifique sua Assinatura",
        subtitle: "Digite seu email para ver o status da sua assinatura Adobe",
        emailPlaceholder: "Digite seu endereço de email",
        searchButton: "Verificar Status",
        searching: "Buscando...",
        organization: "Nome da equipe",
        plan: "Plano Ativo",
        duration: "Duração",
        months: "meses",
        daysRemaining: "Dias Restantes",
        days: "dias",
        status: "Status",
        activated: "Ativado",
        expires: "Expira",
        active: "Ativo",
        expired: "Expirado",
        expiringSoon: "Expirando em Breve",
        notFound: "Nenhuma assinatura encontrada para este email",
        notFoundDesc: "Por favor, verifique seu email ou contate seu revendedor.",
        instructions: "Instruções",
        instructionsText: "Se você recebeu acesso recentemente ou sua organização foi atualizada, saia de todos os aplicativos Adobe e entre novamente com este email. Vá para account.adobe.com e selecione 'Sair de todos os lugares' se necessário.",
        teamSwitch: "Precisa trocar de equipe?",
        teamSwitchText: "Se você vir múltiplas opções de equipe, selecione a organização mostrada acima para acessar sua assinatura.",
        videoTitle: "Como excluir uma organização inválida?",
        notice: "Aviso Importante",
        subscriptionProgress: "Progresso da Assinatura",
        footer: "Para suporte, contate seu revendedor ou administrador.",
        language: "Idioma",
    },
    ar: {
        title: "تحقق من اشتراكك",
        subtitle: "أدخل بريدك الإلكتروني لعرض حالة اشتراك Adobe الخاص بك",
        emailPlaceholder: "أدخل عنوان بريدك الإلكتروني",
        searchButton: "تحقق من الحالة",
        searching: "جاري البحث...",
        organization: "اسم الفريق",
        plan: "الخطة النشطة",
        duration: "المدة",
        months: "أشهر",
        daysRemaining: "الأيام المتبقية",
        days: "أيام",
        status: "الحالة",
        activated: "مفعل",
        expires: "ينتهي",
        active: "نشط",
        expired: "منتهي",
        expiringSoon: "ينتهي قريباً",
        notFound: "لم يتم العثور على اشتراك لهذا البريد",
        notFoundDesc: "يرجى التحقق من بريدك الإلكتروني أو الاتصال بالبائع.",
        instructions: "التعليمات",
        instructionsText: "إذا حصلت على حق الوصول مؤخراً أو تم تحديث مؤسستك، يرجى تسجيل الخروج من جميع تطبيقات Adobe وإعادة تسجيل الدخول بهذا البريد. انتقل إلى account.adobe.com واختر 'تسجيل الخروج من كل مكان' إذا لزم الأمر.",
        teamSwitch: "هل تحتاج لتبديل الفرق؟",
        teamSwitchText: "إذا رأيت خيارات فريق متعددة، حدد المؤسسة المعروضة أعلاه للوصول إلى اشتراكك.",
        videoTitle: "كيف أحذف منظمة غير صالحة؟",
        notice: "إشعار مهم",
        subscriptionProgress: "تقدم الاشتراك",
        footer: "للدعم، اتصل بالبائع أو المسؤول.",
        language: "اللغة",
    },
    zh: {
        title: "查看您的订阅",
        subtitle: "输入您的电子邮件以查看您的Adobe订阅状态",
        emailPlaceholder: "输入您的电子邮件地址",
        searchButton: "查看状态",
        searching: "搜索中...",
        organization: "团队名称",
        plan: "当前计划",
        duration: "时长",
        months: "个月",
        daysRemaining: "剩余天数",
        days: "天",
        status: "状态",
        activated: "激活于",
        expires: "到期",
        active: "有效",
        expired: "已过期",
        expiringSoon: "即将过期",
        notFound: "未找到此邮箱的订阅",
        notFoundDesc: "请检查您的电子邮件地址或联系您的经销商。",
        instructions: "使用说明",
        instructionsText: "如果您最近获得了访问权限或您的组织已更新，请退出所有Adobe应用程序，然后使用此电子邮件重新登录。如有需要，请访问account.adobe.com并选择'退出所有设备'。",
        teamSwitch: "需要切换团队？",
        teamSwitchText: "如果您看到多个团队选项，请选择上方显示的组织以访问您的订阅。",
        videoTitle: "如何删除无效的组织？",
        notice: "重要通知",
        subscriptionProgress: "订阅进度",
        footer: "如需支持，请联系您的经销商或管理员。",
        language: "语言",
    },
    ja: {
        title: "サブスクリプションを確認",
        subtitle: "メールアドレスを入力してAdobeサブスクリプションの状態を確認",
        emailPlaceholder: "メールアドレスを入力",
        searchButton: "ステータスを確認",
        searching: "検索中...",
        organization: "チーム名",
        plan: "アクティブプラン",
        duration: "期間",
        months: "ヶ月",
        daysRemaining: "残り日数",
        days: "日",
        status: "ステータス",
        activated: "有効化",
        expires: "有効期限",
        active: "アクティブ",
        expired: "期限切れ",
        expiringSoon: "まもなく期限切れ",
        notFound: "このメールのサブスクリプションが見つかりません",
        notFoundDesc: "メールアドレスを確認するか、販売店にお問い合わせください。",
        instructions: "手順",
        instructionsText: "最近アクセス権を取得した場合、または組織が更新された場合は、すべてのAdobeアプリからサインアウトし、このメールで再度サインインしてください。必要に応じて、account.adobe.comで「すべての場所からサインアウト」を選択してください。",
        teamSwitch: "チームを切り替える必要がありますか？",
        teamSwitchText: "複数のチームオプションが表示される場合は、上記の組織を選択してサブスクリプションにアクセスしてください。",
        videoTitle: "無効な組織を削除する方法は？",
        notice: "重要なお知らせ",
        subscriptionProgress: "サブスクリプション進捗",
        footer: "サポートが必要な場合は、販売店または管理者にお問い合わせください。",
        language: "言語",
    },
    ko: {
        title: "구독 확인",
        subtitle: "이메일을 입력하여 Adobe 구독 상태를 확인하세요",
        emailPlaceholder: "이메일 주소 입력",
        searchButton: "상태 확인",
        searching: "검색 중...",
        organization: "팀 이름",
        plan: "활성 플랜",
        duration: "기간",
        months: "개월",
        daysRemaining: "남은 일수",
        days: "일",
        status: "상태",
        activated: "활성화",
        expires: "만료",
        active: "활성",
        expired: "만료됨",
        expiringSoon: "곧 만료",
        notFound: "이 이메일에 대한 구독을 찾을 수 없습니다",
        notFoundDesc: "이메일 주소를 확인하거나 리셀러에게 문의하세요.",
        instructions: "안내",
        instructionsText: "최근에 액세스 권한을 받았거나 조직이 업데이트된 경우 모든 Adobe 앱에서 로그아웃한 후 이 이메일로 다시 로그인하세요. 필요한 경우 account.adobe.com에서 '모든 곳에서 로그아웃'을 선택하세요.",
        teamSwitch: "팀을 전환해야 하나요?",
        teamSwitchText: "여러 팀 옵션이 표시되면 위에 표시된 조직을 선택하여 구독에 액세스하세요.",
        videoTitle: "잘못된 조직을 삭제하는 방법은?",
        notice: "중요 공지",
        subscriptionProgress: "구독 진행률",
        footer: "지원이 필요하면 리셀러 또는 관리자에게 문의하세요.",
        language: "언어",
    }
};

const languageNames = {
    en: "English",
    es: "Español",
    fr: "Français",
    de: "Deutsch",
    pt: "Português",
    ar: "العربية",
    zh: "中文",
    ja: "日本語",
    ko: "한국어"
};

function UserStatusPortal() {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [language, setLanguage] = useState('en');
    const [relativeTick, setRelativeTick] = useState(0);

    // Self-service organization switch (shown only when current org is disabled).
    // The target organization is auto-selected server-side from a live,
    // continuously-updated pool — the user just confirms, they don't pick one.
    const [switchOptions, setSwitchOptions] = useState(null); // { available, currentOrgName, targetOrgName, cooldownActive }
    const [switching, setSwitching] = useState(false);
    const [switchPhase, setSwitchPhase] = useState(null); // 'inviting' | 'retry-failed' while switching
    const [switchMessage, setSwitchMessage] = useState(null);
    // Shown in place of the generic "still searching" text after a real attempt
    // exhausted its retries — explains what actually happened instead of the
    // page just silently going quiet, which read as broken.
    const [switchStuckMessage, setSwitchStuckMessage] = useState(null);
    // Progress for the switch banner: which attempt is running, when the next
    // one fires, and a ticking counter so the countdown re-renders each second.
    const [switchAttempt, setSwitchAttempt] = useState(0);
    const [switchRetryAt, setSwitchRetryAt] = useState(null);
    const [switchTick, setSwitchTick] = useState(0);

    // Self-service email change. Offered only when Adobe reports the current
    // address as unusable (Adobe has been disabling customer Adobe IDs, which
    // strands a paid subscription on a dead address). The server decides
    // eligibility and which teams are legal — this is display only.
    const [ecOptions, setEcOptions] = useState(null);
    const [ecOpen, setEcOpen] = useState(false);
    const [ecNewEmail, setEcNewEmail] = useState('');
    const [ecOrgId, setEcOrgId] = useState('');
    const [ecBusy, setEcBusy] = useState(false);
    const [ecError, setEcError] = useState(null);
    const [ecDone, setEcDone] = useState(null);

    const t = translations[language];
    const isRTL = language === 'ar';
    // Resolved only once a lookup finds which reseller owns the email — before
    // that there's no reseller context yet, so the header shows the defaults.
    const branding = result?.branding || {};
    const renderLinkifiedNotice = (text) => {
        const source = String(text || '');
        const urlPattern = /(https?:\/\/[^\s<>"')\]]+)/g;
        const parts = source.split(urlPattern);
        return parts.map((part, index) => {
            urlPattern.lastIndex = 0;
            if (!urlPattern.test(part)) return part;
            urlPattern.lastIndex = 0;
            const trailing = part.match(/[.,;:!?]+$/)?.[0] || '';
            const href = trailing ? part.slice(0, -trailing.length) : part;
            return (
                <React.Fragment key={`${href}-${index}`}>
                    <a href={href} target="_blank" rel="noopener noreferrer" className="status-portal-notice-link">
                        {href}
                    </a>
                    {trailing}
                </React.Fragment>
            );
        });
    };


    // Email-change copy lives here rather than in `translations` so the nine
    // existing language blocks don't each need editing; anything not translated
    // falls back to English instead of rendering blank.
    const ecStrings = {
        en: {
            heading: 'Adobe has blocked this email',
            body: 'Adobe has disabled this address, so it can no longer sign in. Your subscription is still valid — move it to a different email address to keep using it.',
            headingInactive: 'Move this subscription to another email',
            bodyInactive: 'This team is no longer active, so this address cannot be used. Your subscription is still valid — move it to a different email address and an active team to keep using it.',
            unavailableHeading: 'Changing your email',
            cta: 'Change my email address',
            newEmail: 'New email address',
            newEmailPlaceholder: 'you@example.com',
            organization: 'Team Name',
            seats: 'seats free',
            unlimited: 'available',
            current: 'current team',
            submit: 'Move my subscription',
            working: 'Moving your subscription…',
            cancel: 'Cancel',
            cooldown: 'Recently changed — please wait a few minutes',
            note: 'You can only choose teams that match the plan you already paid for.'
        },
        zh: {
            heading: 'Adobe 已封禁此邮箱',
            body: 'Adobe 已停用该邮箱，无法再登录。您的订阅仍然有效 —— 将其转移到其他邮箱即可继续使用。',
            headingInactive: '将此订阅转移到其他邮箱',
            bodyInactive: '该团队已停用，此邮箱无法继续使用。您的订阅仍然有效 —— 请将其转移到其他邮箱及有效团队以继续使用。',
            unavailableHeading: '更改邮箱',
            cta: '更改我的邮箱地址',
            newEmail: '新邮箱地址',
            newEmailPlaceholder: 'you@example.com',
            organization: '团队名称',
            seats: '个可用名额',
            unlimited: '可用',
            current: '当前团队',
            submit: '转移我的订阅',
            working: '正在转移您的订阅…',
            cancel: '取消',
            cooldown: '刚刚更改过 —— 请稍候几分钟',
            note: '您只能选择与已购买方案相符的团队。'
        }
    };
    const ec = (key) => (ecStrings[language] && ecStrings[language][key]) || ecStrings.en[key];

    // Self-service re-invite: re-sends the Adobe invitation for this email into
    // the team it already belongs to. Untranslated languages fall back to English.
    const riStrings = {
        en: {
            heading: "Didn't get your Adobe invitation?",
            body: 'If your invitation expired, was declined, or you were removed from the team, send yourself a fresh one here.',
            cta: 'Resend my invitation',
            working: 'Sending your invitation…'
        },
        zh: {
            heading: '没有收到 Adobe 邀请？',
            body: '如果您的邀请已过期、被拒绝，或您已被移出团队，可在此重新发送邀请。',
            cta: '重新发送邀请',
            working: '正在发送邀请…'
        }
    };
    const ri = (key) => (riStrings[language] && riStrings[language][key]) || riStrings.en[key];
    const [riBusy, setRiBusy] = useState(false);
    const [riResult, setRiResult] = useState(null); // { ok, message }
    // A new lookup must not show the previous email's re-invite outcome.
    useEffect(() => { setRiResult(null); }, [result?.email]);

    const handleReinvite = async () => {
        if (!result?.email || riBusy) return;
        setRiBusy(true);
        setRiResult(null);
        try {
            const res = await fetch(`${API_URL}/credits-pool/public/reinvite`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: result.email.trim() })
            });
            const data = await res.json();
            setRiResult(res.ok && data.success
                ? { ok: true, message: data.message }
                : { ok: false, message: data.error || 'Could not send the invitation. Please try again later.' });
        } catch (err) {
            setRiResult({ ok: false, message: 'Network error. Please try again.' });
        } finally {
            setRiBusy(false);
        }
    };

    const fetchEmailChangeOptions = useCallback(async (emailToLookup) => {
        if (!emailToLookup || !emailToLookup.trim()) return;
        try {
            const res = await fetch(`${API_URL}/credits-pool/public/email-change/options`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: emailToLookup.trim() })
            });
            const data = await res.json();
            // Keep the payload even when ineligible: the card explains WHY
            // instead of vanishing, which just reads as a missing feature.
            setEcOptions(res.ok ? data : null);
            if (res.ok && data.eligible && data.organizations?.length) {
                setEcOrgId(String(data.organizations[0].id));
            }
        } catch {
            setEcOptions(null);
        }
    }, []);

    const submitEmailChange = async () => {
        if (!result?.email || !ecNewEmail.trim()) return;
        setEcBusy(true);
        setEcError(null);
        try {
            const res = await fetch(`${API_URL}/credits-pool/public/email-change`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: result.email.trim(),
                    newEmail: ecNewEmail.trim(),
                    organizationId: ecOrgId
                })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setEcDone(data);
                setEcOpen(false);
                setEcOptions(null);
            } else {
                setEcError(data.error || 'Could not change the email address. Please try again.');
            }
        } catch {
            setEcError('Network error. Please try again.');
        } finally {
            setEcBusy(false);
        }
    };

    // Drives the countdown, and only while a retry is actually scheduled, so an
    // idle page carries no timer.
    useEffect(() => {
        if (!switchRetryAt) return undefined;
        const id = setInterval(() => setSwitchTick(t => t + 1), 1000);
        return () => clearInterval(id);
    }, [switchRetryAt]);

    const fetchSwitchOptions = useCallback(async (emailToLookup) => {
        if (!emailToLookup || !emailToLookup.trim()) return;
        try {
            const res = await fetch(`${API_URL}/credits-pool/public/org-switch/options`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: emailToLookup.trim() })
            });
            const data = await res.json();
            if (res.ok && data.available) {
                setSwitchOptions(data);
            }
        } catch (err) {
            // Non-critical — the switch UI just won't appear.
        }
    }, []);

    // Silent background refresh of the full status (not just the switch sub-check) —
    // used by the polling effect below so a page left open updates on its own when
    // the admin changes something, without the loading spinner/flicker a full
    // lookupEmail() would cause on every tick.
    const refreshStatusSilently = useCallback(async (emailToLookup) => {
        if (!emailToLookup || !emailToLookup.trim()) return;
        try {
            const res = await fetch(`${API_URL}/user-status`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: emailToLookup.trim() })
            });
            const data = await res.json();
            if (res.ok && data.found) {
                setResult(data);
                // Only the inactive-org case can ever have a switch target —
                // skip the extra request for the (likely much larger) population
                // of customers with a perfectly healthy subscription, polling
                // every 20s with their page just left open.
                if (data.organizationStatus?.status === 'inactive') {
                    fetchSwitchOptions(data.email || emailToLookup);
                }
            }
        } catch (err) {
            // Non-critical — the next poll will retry.
        }
    }, [fetchSwitchOptions]);

    // A blocked Adobe account is independent of org health, so this is checked
    // for every found subscription rather than only the inactive-org case.
    useEffect(() => {
        if (result?.found && result?.email && !ecDone) {
            fetchEmailChangeOptions(result.email);
        }
    }, [result?.found, result?.email, ecDone, fetchEmailChangeOptions]);

    // Backoff schedule for automatic retries — a failed attempt is almost
    // always transient (the live pool target filled up between preview and
    // execution, or a momentary network/Adobe hiccup), so we keep trying
    // quietly behind an animated status instead of dumping a technical error
    // on the customer and making them click the button again themselves.
    // A small random jitter is added on top so many customers whose pages
    // happened to load around the same time don't all hammer the backend in
    // lockstep on the same schedule.
    const SELF_SWITCH_RETRY_DELAYS_MS = [2000, 3000, 5000, 8000, 12000, 20000];
    const jittered = (ms) => Math.round(ms * (0.85 + Math.random() * 0.3));
    // One more than the delay list: the first go plus one retry per delay.
    const SELF_SWITCH_MAX_ATTEMPTS = SELF_SWITCH_RETRY_DELAYS_MS.length + 1;
    // Recomputed on every switchTick render, so it counts down in real time.
    const switchRetryCountdown = switchRetryAt
        ? Math.max(0, Math.ceil((switchRetryAt - Date.now()) / 1000))
        : 0;

    const attemptSelfSwitch = async (attempt = 0) => {
        if (!result?.email) return;
        setSwitchPhase('inviting');
        setSwitchAttempt(attempt + 1);
        // No countdown while a request is in flight - only between tries.
        setSwitchRetryAt(null);
        try {
            const res = await fetch(`${API_URL}/credits-pool/public/org-switch`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: result.email.trim() })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setSwitchMessage(data.message || 'You have been moved to the new organization.');
                setSwitchStuckMessage(null);
                setSwitchOptions(null);
                setSwitching(false);
                setSwitchPhase(null);
                setSwitchRetryAt(null);
                setSwitchAttempt(0);
                // Refresh the status view so the new org is reflected.
                lookupEmail(result.email);
                return;
            }
            throw Object.assign(new Error(data.error || 'switch_failed'), { status: res.status, code: data.code });
        } catch (err) {
            // A cooldown is a definite answer, not congestion: retrying cannot
            // succeed until it expires. Blind-retrying it was what made a clear
            // "wait 10 minutes" look like the page had hung on "Inviting you...".
            if (err.code === 'COOLDOWN') {
                setSwitching(false);
                setSwitchPhase(null);
                setSwitchOptions(null);
                setSwitchRetryAt(null);
                setSwitchAttempt(0);
                setSwitchStuckMessage(err.message);
                return;
            }
            if (attempt < SELF_SWITCH_RETRY_DELAYS_MS.length) {
                setSwitchPhase('retry-failed');
                setTimeout(() => setSwitchPhase('inviting'), 1200);
                // Back off harder on a 429. Note this is usually OUR server
                // saying it is busy, not Adobe - either way, piling on makes it
                // worse.
                const baseDelay = err.status === 429
                    ? SELF_SWITCH_RETRY_DELAYS_MS[attempt] * 3
                    : SELF_SWITCH_RETRY_DELAYS_MS[attempt];
                const wait = jittered(baseDelay);
                // Publish when the next attempt lands so the banner can count
                // down to it instead of showing an open-ended spinner.
                setSwitchRetryAt(Date.now() + wait);
                setTimeout(() => attemptSelfSwitch(attempt + 1), wait);
            } else {
                // Quick retries exhausted — drop back to the ambient
                // background poll (fetchSwitchOptions, every 20s) rather than
                // retrying forever. Explain what happened instead of silently
                // going quiet, which read as the page just being broken.
                setSwitching(false);
                setSwitchPhase(null);
                setSwitchOptions(null);
                setSwitchRetryAt(null);
                setSwitchAttempt(0);
                setSwitchStuckMessage(
                    err.status === 503
                        ? "Team switching is temporarily paused while we sort out available space. We'll keep checking automatically — no need to keep retrying yourself."
                        : (err.message && err.message !== 'switch_failed'
                            // Prefer whatever the server actually said; a real
                            // reason beats a generic apology.
                            ? `${err.message} We'll keep checking automatically in the background.`
                            : "We're having trouble moving you to a new organization right now — there's a lot of demand on the few available slots. We'll keep checking automatically in the background.")
                );
            }
        }
    };

    const handleSelfSwitch = () => {
        if (!result?.email || switching) return;
        setSwitching(true);
        setSwitchMessage(null);
        setSwitchStuckMessage(null);
        attemptSelfSwitch(0);
    };

    const lookupEmail = useCallback(async (emailToLookup) => {
        if (!emailToLookup.trim()) return;

        setLoading(true);
        setError(null);
        setResult(null);
        setSwitchOptions(null);
        setSwitchMessage(null);

        try {
            const res = await fetch(`${API_URL}/user-status`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: emailToLookup.trim() })
            });
            const data = await res.json();

            if (!res.ok || !data.found) {
                setError(data.error || 'No subscription found for this email');
            } else {
                setResult(data);
                // Offer self-service org switch when the current org has been disabled.
                if (data.organizationStatus?.status === 'inactive') {
                    fetchSwitchOptions(data.email || emailToLookup);
                }
            }
        } catch (err) {
            setError('Network error. Please try again.');
        }
        setLoading(false);
    }, [fetchSwitchOptions]);

    // Auto-lookup email from URL path: /status/user@example.com
    useEffect(() => {
        const path = window.location.pathname;
        const statusPrefix = '/status/';
        const idx = path.toLowerCase().indexOf(statusPrefix);
        if (idx !== -1) {
            const emailFromUrl = decodeURIComponent(path.substring(idx + statusPrefix.length)).trim();
            if (emailFromUrl && emailFromUrl.includes('@')) {
                setEmail(emailFromUrl);
                lookupEmail(emailFromUrl);
            }
        }
    }, [lookupEmail]);

    useEffect(() => {
        if (!result?.teamChange?.changedAt && !result?.organizationStatus?.lastCheckedAt && !result?.organizationStatus?.updatedAt) {
            return undefined;
        }

        const timer = setInterval(() => setRelativeTick(tick => tick + 1), 30000);
        return () => clearInterval(timer);
    }, [result?.teamChange?.changedAt, result?.organizationStatus?.lastCheckedAt, result?.organizationStatus?.updatedAt]);

    // Keep the WHOLE status live while this page stays open — not just the switch
    // sub-check. A customer who loaded this page (often from a saved/shared link)
    // while their org was still Active would otherwise never see an admin's later
    // change to Inactive (or back to Active, or a new switch becoming available)
    // until they manually re-entered their email and clicked Check Status again.
    // Stops once a switch has actually completed (switchMessage set).
    useEffect(() => {
        if (!result?.email || switchMessage) return undefined;

        const timer = setInterval(() => refreshStatusSilently(result.email), 20000);
        return () => clearInterval(timer);
    }, [result?.email, switchMessage, refreshStatusSilently]);

    const handleSearch = async (e) => {
        e.preventDefault();
        lookupEmail(email);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return '#10b981';
            case 'expiring-soon': return '#f59e0b';
            case 'expired': return '#ef4444';
            default: return '#6b7280';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'active': return t.active;
            case 'expiring-soon': return t.expiringSoon;
            case 'expired': return t.expired;
            default: return status;
        }
    };

    const getOrganizationStatusColor = (status) => {
        switch (status) {
            case 'inactive': return '#ef4444';
            case 'updating': return '#f59e0b';
            default: return '#10b981';
        }
    };

    const formatRelativeTime = (timestamp, tick = 0) => {
        if (!timestamp) return '';
        const startedAt = new Date(timestamp).getTime();
        if (Number.isNaN(startedAt)) return '';

        const diffSeconds = Math.max(0, Math.floor(((Date.now() + (tick * 0)) - startedAt) / 1000));
        if (diffSeconds < 60) return `${diffSeconds || 1} second${diffSeconds === 1 ? '' : 's'} ago`;

        const diffMinutes = Math.floor(diffSeconds / 60);
        if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;

        const diffHours = Math.floor(diffMinutes / 60);
        if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;

        const diffDays = Math.floor(diffHours / 24);
        return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    };

    return (
        <div className="status-portal-page" style={{
            minHeight: '100vh',
            background: '#eef8fd',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '40px 20px',
            direction: isRTL ? 'rtl' : 'ltr'
        }}>
            <div className="status-portal-theme-control" style={{
                position: 'absolute',
                top: '20px',
                right: isRTL ? '20px' : 'auto',
                left: isRTL ? 'auto' : '20px'
            }}>
                <ThemeToggle compact />
            </div>

            {/* Language Selector */}
            <div className="status-portal-language-control" style={{
                position: 'absolute',
                top: '20px',
                right: isRTL ? 'auto' : '20px',
                left: isRTL ? '20px' : 'auto'
            }}>
                <select
                    className="status-portal-language-select"
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    style={{
                        padding: '10px 16px',
                        borderRadius: '8px',
                        border: '1px solid #b9cfda',
                        background: '#ffffff',
                        color: '#16232d',
                        fontSize: '14px',
                        cursor: 'pointer'
                    }}
                >
                    {Object.entries(languageNames).map(([code, name]) => (
                        <option key={code} value={code} style={{ color: '#333' }}>{name}</option>
                    ))}
                </select>
            </div>

            {/* Header */}
            <div className="status-portal-header" style={{ textAlign: 'center', marginBottom: '40px', marginTop: '20px' }}>
                <div className="status-portal-icon" style={{
                    width: '80px',
                    height: '80px',
                    background: '#ffffff',
                    border: '1px solid #d9e6ed',
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                    overflow: 'hidden'
                }}>
                    {branding.logoUrl ? (
                        <img src={`${API_URL}${branding.logoUrl}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                        <span style={{ color: '#0b7da6' }}><Icon name="clipboard" size={38} /></span>
                    )}
                </div>
                <h1 className="status-portal-title" style={{
                    color: '#16232d',
                    fontSize: '28px',
                    fontWeight: '700',
                    marginBottom: '12px'
                }}>{branding.title || t.title}</h1>
                <p className="status-portal-subtitle" style={{
                    color: '#5d6d79',
                    fontSize: '16px',
                    maxWidth: '400px'
                }}>{branding.subtitle || t.subtitle}</p>
            </div>

            {/* Search Card */}
            <div className="status-portal-card" style={{
                background: '#ffffff',
                border: '1px solid #d9e6ed',
                borderRadius: '14px',
                padding: '32px',
                width: '100%',
                maxWidth: '500px',
                boxShadow: '0 1px 2px rgba(22,35,45,0.06)'
            }}>
                <form onSubmit={handleSearch}>
                    <div style={{ position: 'relative' }}>
                        <input
                            className="status-portal-input"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t.emailPlaceholder}
                            style={{
                                width: '100%',
                                padding: '14px 16px',
                                fontSize: '16px',
                                border: '1px solid #b9cfda',
                                borderRadius: '10px',
                                outline: 'none',
                                transition: 'border-color 0.15s ease',
                                boxSizing: 'border-box'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#0b7da6'}
                            onBlur={(e) => e.target.style.borderColor = '#b9cfda'}
                        />
                    </div>
                    <button
                        className="status-portal-submit"
                        type="submit"
                        disabled={loading || !email.trim()}
                        style={{
                            width: '100%',
                            padding: '14px',
                            marginTop: '16px',
                            background: loading ? '#b9cfda' : (branding.brandColor || '#ffa239'),
                            color: branding.brandColor ? 'white' : '#3d2200',
                            border: 'none',
                            borderRadius: '10px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            transition: 'background-color 0.15s ease'
                        }}
                    >
                        {loading ? t.searching : `${t.searchButton}`}
                    </button>
                </form>

                {/* Error State */}
                {error && (
                    <div className="status-portal-error" style={{
                        marginTop: '24px',
                        padding: '20px',
                        background: '#ffefef',
                        borderRadius: '10px',
                        borderLeft: '4px solid #ff5656'
                    }}>
                        <strong style={{ color: '#b02a2a' }}>{error}</strong>
                        <p style={{ color: '#7f1d1d', fontSize: '14px', marginTop: '8px', marginBottom: 0 }}>
                            {t.notFoundDesc}
                        </p>
                    </div>
                )}

                {/* Result Display */}
                {result && (
                    <div className="status-portal-result" style={{ marginTop: '24px' }}>
                        {/* Status Badge */}
                        <div className="status-portal-status-row" style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '20px'
                        }}>
                            <span style={{
                                padding: '8px 20px',
                                borderRadius: '50px',
                                background: getStatusColor(result.status),
                                color: 'white',
                                fontWeight: '600',
                                fontSize: '14px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                {getStatusText(result.status)}
                            </span>
                        </div>

                        {/* Seller notice placed at the top (the server sets noticePlacement
                            'top' only for sellers whose notice should be seen first). */}
                        {branding.notice && branding.noticePlacement === 'top' && (
                            <div className="status-portal-notice" style={{
                                marginBottom: '16px',
                                padding: '20px',
                                borderRadius: '10px',
                                background: '#fdf6d8',
                                border: '1px solid #e7cf55'
                            }}>
                                <p className="status-portal-notice-text" style={{
                                    margin: 0,
                                    color: '#7c2d12',
                                    fontSize: '15px',
                                    lineHeight: '1.7',
                                    whiteSpace: 'pre-wrap',
                                    fontWeight: 500
                                }}>
                                    {renderLinkifiedNotice(branding.notice)}
                                </p>
                            </div>
                        )}

                        {/* Organization Card */}
                        <div className="status-portal-subscription-card" style={{
                            background: '#f6fbfe',
                            border: '1px solid #d9e6ed',
                            borderRadius: '12px',
                            padding: '24px',
                            marginBottom: '16px'
                        }}>
                            <style>{`
                                @keyframes statusPortalSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                                @keyframes statusPortalShake { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-4px); } 75% { transform: translateX(4px); } }
                                @keyframes statusPortalPop { 0% { transform: scale(0.85); opacity: 0; } 60% { transform: scale(1.04); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
                                @keyframes statusPortalPulseGlow { 0%, 100% { box-shadow: 0 0 0 0 rgba(37,99,235,0.5); } 50% { box-shadow: 0 0 0 10px rgba(37,99,235,0); } }
                            `}</style>

                            {/* Switch confirmation shown independently of current org status — by the
                                time it appears, the org we just moved away from is no longer "current",
                                so it must not be gated on organizationStatus.status === 'inactive'. */}
                            {switchMessage && (
                                <div className="status-portal-switch-success" style={{
                                    padding: '14px 16px', background: '#ecfdf5', borderRadius: '12px',
                                    borderLeft: '4px solid #10b981', marginBottom: '16px',
                                    animation: 'statusPortalPop 0.4s ease'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                        <span style={{ fontSize: '18px' }}><Icon name="check" size={16} /></span>
                                        <strong className="status-portal-switch-success-title" style={{ color: '#065f46', fontSize: '14px' }}>Team changed</strong>
                                    </div>
                                    <p className="status-portal-switch-success-body" style={{ color: '#047857', fontSize: '13px', margin: 0, lineHeight: '1.5' }}>{switchMessage}</p>
                                </div>
                            )}

                            {/* Organization identity card. When the org is inactive, its name, the
                                INACTIVE badge, the explanation, and the fix-it button all live inside
                                this one card — so it reads as "this org → is broken → press this" instead
                                of three separate floating pieces that could be mistaken for unrelated
                                or sequential states (e.g. the org name looking like a "new" target). */}
                            {(() => {
                                const isInactive = result.organizationStatus?.status === 'inactive' && !switchMessage;
                                return (
                                    <div className={`status-portal-org-card${isInactive ? ' status-portal-org-card--inactive' : ''}`} style={{
                                        marginBottom: '16px',
                                        padding: isInactive ? '16px' : '0',
                                        borderRadius: isInactive ? '14px' : '0',
                                        background: isInactive ? '#ffefef' : 'transparent',
                                        border: isInactive ? '1px solid #ff5656' : 'none'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                            <div className="status-portal-org-icon" style={{
                                                width: '50px',
                                                height: '50px',
                                                background: isInactive ? '#ff5656' : '#dff4fd',
                                                borderRadius: '12px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'white',
                                                fontSize: '24px',
                                                flexShrink: 0,
                                                overflow: 'hidden'
                                            }}>
                                                {branding.orgIconUrl ? (
                                                    <img src={`${API_URL}${branding.orgIconUrl}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                ) : <Icon name="building" size={26} />}
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div className="status-portal-label status-portal-team-label" style={{
                                                    fontSize: '15px', fontWeight: 800, color: '#f59e0b',
                                                    textTransform: 'uppercase', letterSpacing: '1.2px',
                                                    marginBottom: '4px', paddingBottom: '4px',
                                                    borderBottom: '2px solid rgba(245, 158, 11, 0.35)', display: 'inline-block'
                                                }}>
                                                    {t.organization}
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                    <span className="status-portal-value" style={{ fontSize: '21px', fontWeight: '800', color: '#1e293b' }}>
                                                        {result.organization.name}
                                                    </span>
                                                    {isInactive && (
                                                        <span style={{
                                                            padding: '2px 9px', borderRadius: '999px', background: '#ef4444', color: 'white',
                                                            fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px'
                                                        }}>
                                                            Inactive
                                                        </span>
                                                    )}
                                                </div>
                                                {/* The "Team changed … from X to Y" line was removed on the
                                                    owner's instruction: customers are not shown team history. */}

                                                {/* Transfer History */}
                                                {result.lastTransfer && (
                                                    <div className="status-portal-transfer" style={{
                                                        marginTop: '8px',
                                                        padding: '8px 12px',
                                                        background: 'rgba(255,255,255,0.6)',
                                                        borderRadius: '8px',
                                                        fontSize: '12px',
                                                        color: '#475569',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: '2px',
                                                        border: '1px dashed #cbd5e1'
                                                    }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                                            {/* Check if old and new names are the same */}
                                                            {result.lastTransfer.oldOrganizationName === result.lastTransfer.newOrganizationName ? (
                                                                <>
                                                                    <span style={{ color: '#ef4444', fontWeight: '500' }}>del -</span>
                                                                    <span style={{ textDecoration: 'line-through', color: '#94a3b8' }}>
                                                                        {result.lastTransfer.oldOrganizationName}
                                                                    </span>
                                                                    <span style={{ fontSize: '10px', color: '#64748b', fontStyle: 'italic' }}>(old)</span>
                                                                    <span style={{ color: '#64748b' }}></span>
                                                                    <span style={{ fontWeight: '600', color: '#475569' }}>
                                                                        {result.lastTransfer.newOrganizationName}
                                                                    </span>
                                                                    <span style={{ fontSize: '10px', color: '#10b981', fontStyle: 'italic' }}>(new)</span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <span style={{ textDecoration: 'line-through', color: '#94a3b8' }}>
                                                                        {result.lastTransfer.oldOrganizationName}
                                                                    </span>
                                                                    <span style={{ color: '#64748b' }}></span>
                                                                    <span style={{ fontWeight: '600', color: '#475569' }}>
                                                                        {result.lastTransfer.newOrganizationName}
                                                                    </span>
                                                                </>
                                                            )}
                                                        </div>
                                                        <div style={{ fontSize: '10px', color: '#94a3b8' }}>
                                                            Changed: {new Date(result.lastTransfer.date).toLocaleDateString()}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {isInactive && (
                                            <div style={{ marginTop: '14px' }}>
                                                <p style={{ color: '#9f1239', fontSize: '13px', margin: '0 0 12px', lineHeight: '1.5' }}>
                                                    {result.organizationStatus.message}
                                                </p>
                                                {!switchOptions?.available && (
                                                    <div className="status-portal-switch-waiting" style={{
                                                        padding: '12px 16px', background: 'rgba(255,255,255,0.6)', borderRadius: '12px',
                                                        border: '1px solid #fecaca',
                                                        display: 'flex', alignItems: 'center', gap: '10px'
                                                    }}>
                                                        <span style={{ fontSize: '16px', display: 'inline-flex' }}><Icon name={switchStuckMessage ? 'clock' : 'refresh'} size={16} /></span>
                                                        <p style={{ color: '#7f1d1d', fontSize: '13px', margin: 0, lineHeight: '1.5' }}>
                                                            {switchStuckMessage || 'Looking for an available organization — checks automatically, no need to refresh.'}
                                                        </p>
                                                    </div>
                                                )}
                                                {switchOptions?.available && (
                                                    switching ? (
                                                        <div className="status-portal-switch-progress" style={{
                                                            display: 'flex', alignItems: 'center', gap: '10px', padding: '14px',
                                                            background: 'rgba(255,255,255,0.7)', borderRadius: '10px',
                                                            animation: switchPhase === 'retry-failed' ? 'statusPortalShake 0.4s ease' : 'none'
                                                        }}>
                                                            <span style={{
                                                                fontSize: '18px', display: 'inline-block',
                                                                animation: switchPhase === 'inviting' ? 'statusPortalSpin 1s linear infinite' : 'none'
                                                            }}><Icon name={switchPhase === 'retry-failed' ? 'alert' : 'refresh'} size={15} /></span>
                                                            <span style={{ color: '#7f1d1d', fontSize: '14px', fontWeight: 600, lineHeight: 1.5 }}>
                                                                {switchPhase === 'retry-failed'
                                                                    ? 'That team was busy — trying again…'
                                                                    : `Inviting you to ${switchOptions.targetOrgName || 'a new organization'}…`}
                                                                <span style={{ display: 'block', fontWeight: 500, fontSize: '12px', opacity: 0.85, marginTop: '3px' }}>
                                                                    {switchRetryAt
                                                                        ? `Next try in ${switchRetryCountdown}s — attempt ${switchAttempt} of ${SELF_SWITCH_MAX_ATTEMPTS}`
                                                                        : `Attempt ${switchAttempt || 1} of ${SELF_SWITCH_MAX_ATTEMPTS} — the team is busy, this can take up to a minute.`}
                                                                </span>
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={handleSelfSwitch}
                                                            disabled={switchOptions.cooldownActive}
                                                            style={{
                                                                width: '100%', boxSizing: 'border-box',
                                                                padding: '16px 18px', borderRadius: '12px', border: 'none', cursor: switchOptions.cooldownActive ? 'not-allowed' : 'pointer',
                                                                background: switchOptions.cooldownActive ? '#93c5fd' : (branding.brandColor || '#2563eb'), color: 'white', fontSize: '16px', fontWeight: 700,
                                                                animation: switchOptions.cooldownActive ? 'none' : 'statusPortalPulseGlow 1.8s ease-in-out infinite'
                                                            }}
                                                        >
                                                            {switchOptions.cooldownActive ? 'Recently switched — please wait' : 'Press here to change to an active organization'}
                                                        </button>
                                                    )
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                            {/* Updating / Normal published status keeps its own simple notice — only
                                the inactive case is merged above, since that's the one paired with the
                                self-switch action that caused confusion. */}
                            {result.organizationStatus && result.organizationStatus.status !== 'inactive' && (
                                <div className="status-portal-notice" style={{
                                    padding: '14px 16px',
                                    background: 'white',
                                    borderRadius: '12px',
                                    borderLeft: `4px solid ${getOrganizationStatusColor(result.organizationStatus.status)}`,
                                    marginBottom: '16px',
                                    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.08)'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '6px' }}>
                                        <strong style={{ color: '#1e293b', fontSize: '14px' }}>
                                            Team status: {result.organizationStatus.label || result.organizationStatus.status}
                                        </strong>
                                        <span style={{
                                            padding: '3px 10px',
                                            borderRadius: '999px',
                                            background: getOrganizationStatusColor(result.organizationStatus.status),
                                            color: 'white',
                                            fontSize: '11px',
                                            fontWeight: 700,
                                            textTransform: 'uppercase'
                                        }}>
                                            {result.organizationStatus.label || result.organizationStatus.status}
                                        </span>
                                    </div>
                                    <p style={{ color: '#475569', fontSize: '13px', margin: 0, lineHeight: '1.5' }}>
                                        {result.organizationStatus.message}
                                    </p>
                                    {result.organizationStatus.lastCheckedAt && (
                                        <div style={{ color: '#94a3b8', fontSize: '11px', marginTop: '6px' }}>
                                            Checked {formatRelativeTime(result.organizationStatus.lastCheckedAt, relativeTick)}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Admin Notice */}
                            {result.notice && !result.migration && (
                                <div className="status-portal-notice" style={{
                                    padding: '14px 16px',
                                    background: '#fdf6d8',
                                    borderRadius: '10px',
                                    borderLeft: '4px solid #ffa239',
                                    marginBottom: '16px'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                        <span style={{ fontSize: '18px' }}><Icon name="alert" size={16} /></span>
                                        <strong style={{ color: '#92400e', fontSize: '14px' }}>{t.notice}</strong>
                                    </div>
                                    <p style={{ color: '#78350f', fontSize: '13px', margin: 0, lineHeight: '1.5' }}>
                                        {result.notice}
                                    </p>
                                </div>
                            )}

                            {/* Products */}
                            <div style={{ marginBottom: '16px' }}>
                                <div className="status-portal-label" style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    {t.plan}
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    {/* Deduped on render too: rows written before the
                                        storage fix can still hold the same product twice. */}
                                    {[...new Set(result.products)].map((product, idx) => (
                                        <span key={idx} className="status-portal-product-pill" style={{
                                            padding: '6px 14px',
                                            background: 'white',
                                            borderRadius: '8px',
                                            fontSize: '13px',
                                            fontWeight: '500',
                                            color: '#475569',
                                            border: '1px solid #e2e8f0'
                                        }}>{product}</span>
                                    ))}
                                </div>
                            </div>

                            {result.pendingActivation && (
                                <div style={{
                                    marginBottom: '16px', padding: '14px 16px', borderRadius: '12px',
                                    background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: '14px', lineHeight: 1.5
                                }}>
                                    <strong style={{ display: 'block', marginBottom: '4px' }}>⏳ Activation in progress</strong>
                                    Your subscription is saved. Our team is adding your email to Adobe now — this usually takes 5–10 minutes.
                                    You will then receive the Adobe invitation email.
                                </div>
                            )}

                            {/* Self-service re-invite — for an active subscription on a
                                working team. The server re-sends only to this same email
                                and team, and rate-limits it. */}
                            {SHOW_SELF_REINVITE && String(result.status || '').toLowerCase() === 'active'
                                && result.organizationStatus?.status !== 'inactive' && (
                                <div className="status-portal-reinvite" style={{
                                    marginBottom: '16px', padding: '16px', borderRadius: '12px',
                                    background: '#eff6ff', border: '1px solid #bfdbfe'
                                }}>
                                    <strong style={{ display: 'block', color: '#1e3a8a', fontSize: '14px', marginBottom: '4px' }}>
                                        {ri('heading')}
                                    </strong>
                                    <p style={{ margin: '0 0 12px', color: '#475569', fontSize: '12.5px', lineHeight: 1.6 }}>
                                        {ri('body')}
                                    </p>
                                    {riResult && (
                                        <p style={{
                                            margin: '0 0 12px', padding: '10px 12px', borderRadius: '8px', fontSize: '13px', lineHeight: 1.6,
                                            background: riResult.ok ? '#ecfdf5' : '#fef2f2',
                                            color: riResult.ok ? '#065f46' : '#991b1b',
                                            border: `1px solid ${riResult.ok ? '#6ee7b7' : '#fecaca'}`
                                        }}>
                                            {riResult.message}
                                        </p>
                                    )}
                                    {!(riResult && riResult.ok) && (
                                        <button type="button" onClick={handleReinvite} disabled={riBusy}
                                            style={{
                                                width: '100%', padding: '11px', borderRadius: '10px', border: 'none',
                                                background: riBusy ? '#93c5fd' : '#2563eb', color: 'white',
                                                fontSize: '14px', fontWeight: 600, cursor: riBusy ? 'default' : 'pointer'
                                            }}>
                                            {riBusy ? ri('working') : ri('cta')}
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* Self-service email change — only rendered when the server
                                confirmed against Adobe that this address can no longer
                                be used. Sits directly below Active Plan. */}
                            {ecDone && (
                                <div className="status-portal-emailchange-done" style={{
                                    marginBottom: '16px', padding: '18px', borderRadius: '12px',
                                    background: '#ecfdf5', border: '1px solid #6ee7b7'
                                }}>
                                    <p style={{ margin: 0, color: '#065f46', fontSize: '14px', lineHeight: 1.6, fontWeight: 600 }}>
                                        {ecDone.message}
                                    </p>
                                </div>
                            )}

                            {!ecDone && ecOptions && !ecOptions.eligible && ecOptions.reason && (
                                <div className="status-portal-emailchange-blocked" style={{
                                    marginBottom: '16px', padding: '16px', borderRadius: '12px',
                                    background: '#f8fafc', border: '1px solid #e2e8f0'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '6px' }}>
                                        <Icon name="alert" size={15} />
                                        <strong style={{ color: '#475569', fontSize: '13.5px' }}>{ec('unavailableHeading')}</strong>
                                    </div>
                                    <p style={{ margin: 0, color: '#64748b', fontSize: '12.5px', lineHeight: 1.6 }}>
                                        {ecOptions.reason}
                                    </p>
                                </div>
                            )}

                            {!ecDone && ecOptions?.eligible && (
                                <div className="status-portal-emailchange" style={{
                                    marginBottom: '16px', padding: '18px', borderRadius: '12px',
                                    background: '#fff7ed', border: '1px solid #fdba74'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '8px' }}>
                                        <Icon name="alert" size={16} />
                                        <strong style={{ color: '#9a3412', fontSize: '14px' }}>
                                            {ecOptions.blockedStatus === 'team_inactive' ? ec('headingInactive') : ec('heading')}
                                        </strong>
                                    </div>
                                    <p style={{ margin: '0 0 14px', color: '#7c2d12', fontSize: '13px', lineHeight: 1.6 }}>
                                        {ecOptions.blockedStatus === 'team_inactive' ? ec('bodyInactive') : ec('body')}
                                    </p>

                                    {!ecOpen ? (
                                        <button
                                            onClick={() => setEcOpen(true)}
                                            disabled={ecOptions.cooldownActive}
                                            style={{
                                                width: '100%', boxSizing: 'border-box', padding: '14px 18px',
                                                borderRadius: '12px', border: 'none',
                                                cursor: ecOptions.cooldownActive ? 'not-allowed' : 'pointer',
                                                background: ecOptions.cooldownActive ? '#fdba74' : (branding.brandColor || '#ea580c'),
                                                color: 'white', fontSize: '15px', fontWeight: 700
                                            }}
                                        >
                                            {ecOptions.cooldownActive ? ec('cooldown') : ec('cta')}
                                        </button>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            <label style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                                <span style={{ fontSize: '12px', fontWeight: 600, color: '#7c2d12' }}>{ec('newEmail')}</span>
                                                <input
                                                    type="email"
                                                    value={ecNewEmail}
                                                    onChange={(e) => setEcNewEmail(e.target.value)}
                                                    placeholder={ec('newEmailPlaceholder')}
                                                    disabled={ecBusy}
                                                    dir="ltr"
                                                    style={{
                                                        padding: '12px 14px', borderRadius: '10px',
                                                        border: '1px solid #fdba74', fontSize: '14px', width: '100%',
                                                        boxSizing: 'border-box', background: 'white'
                                                    }}
                                                />
                                            </label>

                                            <label style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                                <span style={{ fontSize: '12px', fontWeight: 600, color: '#7c2d12' }}>{ec('organization')}</span>
                                                <select
                                                    value={ecOrgId}
                                                    onChange={(e) => setEcOrgId(e.target.value)}
                                                    disabled={ecBusy}
                                                    style={{
                                                        padding: '12px 14px', borderRadius: '10px',
                                                        border: '1px solid #fdba74', fontSize: '14px', width: '100%',
                                                        boxSizing: 'border-box', background: 'white'
                                                    }}
                                                >
                                                    {(ecOptions.organizations || []).map((o) => (
                                                        <option key={o.id} value={o.id}>
                                                            {o.name}
                                                            {o.isCurrent ? ` — ${ec('current')}` : ''}
                                                            {o.seatsAvailable === null
                                                                ? ` (${ec('unlimited')})`
                                                                : ` (${o.seatsAvailable} ${ec('seats')})`}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>

                                            <p style={{ margin: 0, fontSize: '11.5px', color: '#9a3412', lineHeight: 1.5 }}>
                                                {ec('note')}
                                            </p>

                                            {ecError && (
                                                <p style={{ margin: 0, fontSize: '12.5px', color: '#b91c1c', lineHeight: 1.5 }}>
                                                    {ecError}
                                                </p>
                                            )}

                                            <div style={{ display: 'flex', gap: '10px' }}>
                                                <button
                                                    onClick={submitEmailChange}
                                                    disabled={ecBusy || !ecNewEmail.trim()}
                                                    style={{
                                                        flex: 1, padding: '13px 16px', borderRadius: '10px', border: 'none',
                                                        cursor: (ecBusy || !ecNewEmail.trim()) ? 'not-allowed' : 'pointer',
                                                        background: (ecBusy || !ecNewEmail.trim()) ? '#fdba74' : (branding.brandColor || '#ea580c'),
                                                        color: 'white', fontSize: '14px', fontWeight: 700
                                                    }}
                                                >
                                                    {ecBusy ? ec('working') : ec('submit')}
                                                </button>
                                                <button
                                                    onClick={() => { setEcOpen(false); setEcError(null); }}
                                                    disabled={ecBusy}
                                                    style={{
                                                        padding: '13px 16px', borderRadius: '10px',
                                                        border: '1px solid #fdba74', cursor: ecBusy ? 'not-allowed' : 'pointer',
                                                        background: 'white', color: '#9a3412', fontSize: '14px', fontWeight: 600
                                                    }}
                                                >
                                                    {ec('cancel')}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Seller customer notice. Shown below Active Plan and above
                                subscription timing/progress details. */}
                            {branding.notice && branding.noticePlacement !== 'top' && (
                                <div className="status-portal-notice" style={{
                                    marginBottom: '16px',
                                    padding: '20px',
                                    borderRadius: '10px',
                                    background: '#fdf6d8',
                                    border: '1px solid #e7cf55'
                                }}>
                                    <p className="status-portal-notice-text" style={{
                                        margin: 0,
                                        color: '#7c2d12',
                                        fontSize: '15px',
                                        lineHeight: '1.7',
                                        whiteSpace: 'pre-wrap',
                                        fontWeight: 500
                                    }}>
                                        {renderLinkifiedNotice(branding.notice)}
                                    </p>
                                </div>
                            )}


                            {/* Duration & Days - Only show if showExpirationInfo is enabled */}
                            {result.showExpirationInfo && (result.duration || result.durationLabel) && (
                                <div className="status-portal-metric-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    <div className="status-portal-metric-card" style={{ background: 'white', padding: '16px', borderRadius: '12px', textAlign: 'center' }}>
                                        <div className="status-portal-label" style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            {t.duration}
                                        </div>
                                        <div className="status-portal-metric-value" style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b' }}>
                                            {result.durationLabel || `${result.duration} ${t.months}`}
                                        </div>
                                    </div>
                                    <div className="status-portal-metric-card" style={{
                                        background: 'white',
                                        padding: '16px',
                                        borderRadius: '12px',
                                        textAlign: 'center',
                                        borderLeft: `4px solid ${getStatusColor(result.status)}`
                                    }}>
                                        <div className="status-portal-label" style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            {t.daysRemaining}
                                        </div>
                                        <div className="status-portal-metric-value" style={{ fontSize: '24px', fontWeight: '700', color: getStatusColor(result.status) }}>
                                            {result.daysRemaining > 0 ? result.daysRemaining : 0} <span style={{ fontSize: '14px', fontWeight: '400' }}>{t.days}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Progress Bar - Only show if showExpirationInfo is enabled */}
                        {result.showExpirationInfo && result.activatedAt && result.expiresAt && (
                            <div style={{ marginBottom: '16px' }}>
                                <div className="status-portal-label" style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>
                                    {t.subscriptionProgress} ({result.progressPercent || 0}%)
                                </div>
                                <div className="status-portal-progress-track" style={{
                                    height: '8px',
                                    background: '#e2e8f0',
                                    borderRadius: '4px',
                                    overflow: 'hidden'
                                }}>
                                    <div style={{
                                        width: `${result.progressPercent || 0}%`,
                                        height: '100%',
                                        background: getStatusColor(result.status),
                                        borderRadius: '4px',
                                        transition: 'width 0.5s ease'
                                    }}></div>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '11px', color: '#94a3b8' }}>
                                    <span>{t.activated}: {new Date(result.activatedAt).toLocaleDateString()}</span>
                                    <span>{t.expires}: {new Date(result.expiresAt).toLocaleDateString()}</span>
                                </div>
                            </div>
                        )}

                        {/* Migration Status - Eye-catching animated UI */}
                        {result.migration && (
                            <div style={{
                                background: '#ffffff',
                                border: '1px solid #d9e6ed',
                                borderRadius: '12px',
                                padding: '24px',
                                marginBottom: '16px',
                                position: 'relative'
                            }}>
                                {/* Status Badge */}
                                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px', position: 'relative' }}>
                                    <span style={{
                                        padding: '6px 18px',
                                        borderRadius: '50px',
                                        background: '#dff4fd',
                                        color: '#0b7da6',
                                        fontWeight: '700',
                                        fontSize: '12px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '1px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        <span style={{
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '50%',
                                            background: '#1b8a5a'
                                        }} />
                                        {t.migrationActive || 'MIGRATING'}
                                    </span>
                                </div>

                                {/* Title */}
                                <h3 style={{
                                    color: '#16232d',
                                    fontSize: '20px',
                                    fontWeight: '700',
                                    textAlign: 'center',
                                    marginBottom: '8px',
                                    position: 'relative'
                                }}>{t.migrationInProgress || 'Account move in progress'}</h3>
                                <p style={{
                                    color: '#5d6d79',
                                    fontSize: '14px',
                                    textAlign: 'center',
                                    marginBottom: '24px',
                                    position: 'relative'
                                }}>{t.migrationSubtitle || 'Your account is being moved to a new system'}</p>

                                {/* Progress Stats Grid */}
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(3, 1fr)',
                                    gap: '12px',
                                    marginBottom: '20px',
                                    position: 'relative'
                                }}>
                                    <div style={{
                                        background: '#f6fbfe',
                                        border: '1px solid #d9e6ed',
                                        borderRadius: '10px',
                                        padding: '16px',
                                        textAlign: 'center'
                                    }}>
                                        <div style={{ fontSize: '11px', color: '#5d6d79', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                                            {t.queuePosition || 'Queue Position'}
                                        </div>
                                        <div style={{ fontSize: '26px', fontWeight: '700', color: '#16232d' }}>
                                            #{result.migration.queuePosition}
                                        </div>
                                    </div>
                                    <div style={{
                                        background: '#f6fbfe',
                                        border: '1px solid #d9e6ed',
                                        borderRadius: '10px',
                                        padding: '16px',
                                        textAlign: 'center'
                                    }}>
                                        <div style={{ fontSize: '11px', color: '#5d6d79', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                                            {t.estimatedTime || 'Estimated Time'}
                                        </div>
                                        <div style={{ fontSize: '26px', fontWeight: '700', color: '#16232d' }}>
                                            ~{result.migration.estimatedMinutesLeft} <span style={{ fontSize: '14px', fontWeight: '400' }}>{t.minutes || 'min'}</span>
                                        </div>
                                    </div>
                                    <div style={{
                                        background: '#f6fbfe',
                                        border: '1px solid #d9e6ed',
                                        borderRadius: '10px',
                                        padding: '16px',
                                        textAlign: 'center'
                                    }}>
                                        <div style={{ fontSize: '11px', color: '#5d6d79', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                                            {t.usersProcessed || 'Users Processed'}
                                        </div>
                                        <div style={{ fontSize: '26px', fontWeight: '700', color: '#16232d' }}>
                                            {result.migration.migratedCount}<span style={{ fontSize: '14px', fontWeight: '400', color: '#5d6d79' }}>/{result.migration.totalUsers}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Progress Bar */}
                                <div style={{ marginBottom: '16px', position: 'relative' }}>
                                    <div style={{
                                        height: '10px',
                                        background: '#d9e6ed',
                                        borderRadius: '5px',
                                        overflow: 'hidden'
                                    }}>
                                        <div style={{
                                            width: `${Math.round((result.migration.migratedCount / result.migration.totalUsers) * 100)}%`,
                                            height: '100%',
                                            background: '#1b8a5a',
                                            borderRadius: '5px',
                                            transition: 'width 0.5s ease'
                                        }} />
                                    </div>
                                    <div style={{ textAlign: 'center', marginTop: '8px', color: '#5d6d79', fontSize: '13px' }}>
                                        {Math.round((result.migration.migratedCount / result.migration.totalUsers) * 100)}% complete
                                    </div>
                                </div>

                                {/* Please Wait Message */}
                                <p style={{
                                    color: '#5d6d79',
                                    fontSize: '13px',
                                    textAlign: 'center',
                                    marginBottom: 0,
                                    position: 'relative'
                                }}>{t.pleaseWait || 'Please wait while we complete your migration. This process is automatic.'}</p>

                                {/* CSS Keyframes */}
                                <style>{`
                                    @keyframes pulse {
                                        0%, 100% { opacity: 0.5; transform: scale(1); }
                                        50% { opacity: 1; transform: scale(1.05); }
                                    }
                                    @keyframes blink {
                                        0%, 100% { opacity: 1; }
                                        50% { opacity: 0.3; }
                                    }
                                `}</style>
                            </div>
                        )}

                        {/* Notice moved to top of Organization Card */}

                        {/* Instructions */}
                        <div className="status-portal-help-card" style={{
                            padding: '20px',
                            background: '#f0f9ff',
                            borderRadius: '12px',
                            borderLeft: '4px solid #0ea5e9'
                        }}>
                            <strong style={{ color: '#0369a1' }}>{t.instructions}</strong>
                            <p style={{ color: '#075985', fontSize: '13px', marginTop: '8px', lineHeight: '1.6' }}>
                                {t.instructionsText}
                            </p>
                            <div className="status-portal-help-divider" style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #bae6fd' }}>
                                <strong style={{ color: '#0369a1', fontSize: '13px' }}>{t.teamSwitch}</strong>
                                <p style={{ color: '#075985', fontSize: '13px', marginTop: '4px', marginBottom: 0 }}>
                                    {t.teamSwitchText}
                                </p>
                            </div>
                        </div>

                        {/* Video Tutorial */}
                        <div className="status-portal-video-card" style={{
                            marginTop: '16px',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0'
                        }}>
                            <div className="status-portal-video-header" style={{ padding: '12px 16px', background: '#f1f5f9' }}>
                                <strong style={{ color: '#334155', fontSize: '14px' }}>{t.videoTitle || 'How to activate your subscription'}</strong>
                            </div>
                            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                                <iframe
                                    src="https://odysee.com/$/embed/Clean-up-invalid-Adobe-organizations:cc79a3cb61"
                                    title="How to activate your subscription"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%'
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Footer */}
            <p className="status-portal-footer" style={{
                marginTop: '32px',
                color: '#5d6d79',
                fontSize: '13px'
            }}>{t.footer}</p>
        </div>
    );
}

export default UserStatusPortal;
